import React from 'react';
import { useField } from 'react-final-form';
import { FormattedMessage } from 'react-intl';
import { Resizable } from 'react-resizable';

import { SIGNATURE_BOX_MIN_HEIGHT, SIGNATURE_BOX_MIN_WIDTH, SIGNATURE_BOX_RATIO } from './constants';
import useSignZoneHandlers from './hooks/useSignZoneHandlers';
import type { SignatureData, SignatureFieldValue, ZoneData } from './hooks/useSignZoneTypes';
import { DeleteButton, ResizeIcon, SignContainer, SignInfo, SignZone, SignZoneTooltip } from './UI';

type SignatureSelectionProps = {
  name: string;
  page: number;
  pdfRef: React.RefObject<HTMLCanvasElement | null>;
  scale?: number;
  onClick?: (event?: React.MouseEvent) => void;
};

const SignatureSelection: React.FC<SignatureSelectionProps> = ({ name, page, pdfRef, scale = 1, onClick = () => {} }) => {
  const signZoneRef = React.useRef<HTMLDivElement>(null);
  const tooltipRef = React.useRef<HTMLDivElement>(null);
  const { value: signatureData, onChange: setSignatureData } = useField<SignatureFieldValue>(name, {
    subscription: { value: true },
    format: (value) => value,
  }).input;
  const hasSignZone = signatureData?.page === page - 1 && signatureData?.topLeft?.x >= 0;

  const handleChange = (data: SignatureData) => {
    setSignatureData({ ...data, page: page - 1 });
  };

  const {
    data,
    resizeHandlers,
    signZoneHandlers,
    isDragging,
    isResizing,
    minPageSize,
  } = useSignZoneHandlers({
    tooltipRef,
    pdfRef,
    signZoneRef,
    hasSignZone,
    name,
    scale,
    onClick,
    onChange: handleChange,
  });

  const stopPropagation = (event: React.MouseEvent) => {
    event.stopPropagation();
  };

  const handleDelete = (event: React.MouseEvent) => {
    stopPropagation(event);
    if (!isDragging && !isResizing) {
      setSignatureData({} as SignatureFieldValue);
    }
  };

  return (
    <>
      {hasSignZone && (
        <SignContainer {...(data as ZoneData)} ref={signZoneRef}>
          <Resizable
            {...(data as ZoneData)}
            lockAspectRatio
            {...resizeHandlers}
            minConstraints={[SIGNATURE_BOX_MIN_WIDTH, SIGNATURE_BOX_MIN_HEIGHT]}
            draggableOpts={{ grid: SIGNATURE_BOX_RATIO }}
            handle={<ResizeIcon><i className="fb-icon--resize" /></ResizeIcon>}
          >
            <SignZone data-cy="signature-signZone" {...signZoneHandlers} className={(data.width ?? 0) < 120 ? 'small' : undefined}>
              <DeleteButton className="d-flex justify-content-center align-items-center" shape="circle" size="small" data-cy="signature-signZone-delete-btn" icon={<span className="fb-icon--close text-base d-flex" />} onMouseUp={handleDelete} onMouseMove={stopPropagation} onMouseDown={stopPropagation} />
              <div className="placeholder">
                <FormattedMessage id="checkIn.mobility.contract.modal.signHere" defaultMessage="Sign here" />
              </div>
            </SignZone>
          </Resizable>
          <SignInfo minPageSize={minPageSize} {...(data as ZoneData)}>
            <FormattedMessage id="checkIn.mobility.contract.modal.firstnameLastname" defaultMessage="First name LAST NAME" />
            <FormattedMessage id="checkIn.mobility.contract.modal.datetime" defaultMessage="Date and time of signature" />
          </SignInfo>
        </SignContainer>
      )}
      {!hasSignZone && <SignZoneTooltip className="d-none" ref={tooltipRef}><FormattedMessage id="checkIn.mobility.contract.modal.signZoneTooltip" defaultMessage="Add the signature zone" /></SignZoneTooltip>}
    </>
  );
};

export default SignatureSelection;
